import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItemText,
  Divider,
  IconButton,
  Avatar,
  Badge,
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  AppBar,
  Toolbar,
} from "@mui/material";
import DOMPurify from "dompurify";
import { API_URL } from "../utils/config";
import {
  Notifications,
  CheckCircle,
  Assignment,
  Update,
  PersonOutlineOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";

const RightSidebar = () => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageData, setImageData] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userDepartment, setUserDepartment] = useState(null);

  useEffect(() => {
    // First fetch the user's department
    getUserDepartment();
  }, []);

  useEffect(() => {
    // Only fetch notifications after we have the user's department
    if (userDepartment !== null) {
      getNotifications();
    }
  }, [userDepartment]);

  async function getUserDepartment() {
    const token = localStorage.getItem("token") || "";
    const userId = localStorage.getItem("userId") || "";

    try {
      const response = await fetch(
        `${API_URL}/user-department/${userId}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setUserDepartment(result.department);
      } else {
        console.error("Failed to fetch user department:", response.statusText);
        // Set to empty string to proceed with notifications fetch
        setUserDepartment("");
      }
    } catch (error) {
      console.error("Error fetching user department:", error);
      // Set to empty string to proceed with notifications fetch
      setUserDepartment("");
    }
  }

  async function getNotifications() {
    const token = localStorage.getItem("token") || "";
    const company = localStorage.getItem("company") || "";

    try {
      const response = await fetch(
        `${API_URL}/notifications/${company}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();

        // Filter notifications based on department
        const filteredNotifications = result.filter((notification) => {
          // Show notification if:
          // 1. It has no department specified (null/undefined/"")
          // 2. OR its department matches the user's department
          return (
            !notification.department ||
            notification.department === "" ||
            notification.department === userDepartment
          );
        });

        setImageData(filteredNotifications);
      } else {
        console.error("Failed to fetch notifications:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}
    >
      {/* Header - Bell Icon & Avatar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Badge badgeContent={imageData.length} color="error">
          <IconButton>
            <Notifications fontSize="large" sx={{ color: "#E1BA66" }} />
          </IconButton>
        </Badge>
        <Avatar
          src={
            localStorage.getItem("avatar") &&
            localStorage.getItem("avatar").includes("uploads")
              ? `${API_URL}/${localStorage.getItem("avatar")}`
              : localStorage.getItem("avatar")
                ? require(`../assets/a${localStorage.getItem("avatar")}.png`)
                : undefined
          } // Replace with dynamic URL logic if needed
          sx={{
            border: "2px solid #E1BA66",
            bgcolor: "transparent",
            color: "#fff",
          }}
          onClick={() => document.getElementById("avatar-upload").click()} // Triggers the file input
        />
      </Box>

      <List sx={{ overflowY: "auto", flexGrow: 1 }}>
        {imageData.slice(0, 5).map((notification) => (
          <Card
            key={notification.id}
            onClick={() => handleNotificationClick(notification)}
            sx={{
              mb: 1,
              p: 2,
              borderRadius: 3,
              boxShadow: 3,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              transition: "0.3s",
              border: "1px solid #E1BA66",
              background:
                "linear-gradient(135deg,rgb(253, 253, 253),rgb(255, 255, 255))",
              color: "white",
              "&:hover": { transform: "scale(1.05)", boxShadow: 6 },
            }}
          >
            <IconButton sx={{ color: "#E1BA66", mr: 1 }}>
              <CheckCircle />
            </IconButton>
            <ListItemText
              style={{
                fontSize: "14px",
                fontFamily: "Noto Sans",
                margin: 0,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.4",
                color: "#E1BA66",
              }}
              primary={notification.subject}
            />
          </Card>
        ))}
      </List>

      {/* Full Screen Popup */}
      <Dialog
        fullScreen
        open={isPopupOpen}
        onClose={handleClosePopup}
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "background.default",
          },
        }}
      >
        <AppBar
          sx={{
            position: "relative",
            background: "#000000", // Black background
            boxShadow: "0px 1px 3px rgba(225, 186, 102, 0.3)", // Subtle golden shadow
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              sx={{ color: "#E1BA66" }} // Golden color for icon
              onClick={handleClosePopup}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{ ml: 2, flex: 1, color: "#E1BA66", fontWeight: 500 }}
              variant="h6"
            >
              Notification Details
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 4 }}>
          {selectedNotification && (
            <Box sx={{ maxWidth: "800px", margin: "0 auto" }}>
              <Card sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
                <Box
                  sx={{
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& a": { color: "#4facfe", textDecoration: "none" },
                    "& a:hover": { textDecoration: "underline" },
                    "& p": { margin: "0.5em 0" },
                    "& h1, h2, h3, h4, h5, h6": {
                      color: "#4facfe",
                      fontWeight: "bold",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      selectedNotification.description,
                    ),
                  }}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.7 }}>
                  {selectedNotification.message}
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    alignItems: "center",
                    color: "text.secondary",
                  }}
                >
                  <Update sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body2">
                    {new Date(selectedNotification.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Card>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

export default RightSidebar;
